import React, { useEffect, useState } from 'react'
import { ComponentConfig } from '@puckeditor/core'
import { resolveCurrentLanguage } from './resolveCurrentLanguage'
import { LANGUAGE_NAMES } from '../LanguageSwitcher/languages'
import { BreakSlugField, normalizeEntries, BreakSlugEntry } from './BreakSlugField'
// @ts-ignore - JSX component, no type declarations
import { BreakExperience } from '../../../templates/ScrollingSponsoredTemplate/components/BreakExperience'

// Load the break's data from the build-time /embed page-data Gatsby already
// generates. Same-origin, so it sidesteps the API's CORS + staging QA gate,
// and it's the exact `pageContext.brk` the /break and /embed pages render.
async function loadBreakBySlug(slug: string): Promise<any> {
  const res = await fetch(
    `/page-data/embed/${encodeURIComponent(slug)}/page-data.json`,
  )
  if (!res.ok) throw new Error(`page-data for /embed/${slug} → HTTP ${res.status}`)
  const json = await res.json()
  const brk = json?.result?.pageContext?.brk
  if (!brk) throw new Error(`no break data in page-data for /embed/${slug}`)
  return brk
}

export type BreakBlockProps = {
  slugs: BreakSlugEntry[]
  /** Embed mode: guests are auto-registered and never prompted to sign up.
   * Turn off only on sites with a real account flow. */
  embedMode?: boolean
}

const BreakRender = (props: BreakBlockProps & { puck?: any }) => {
  const { slugs, embedMode = true, puck } = props
  const isEditing: boolean = puck?.isEditing ?? false
  const entries = normalizeEntries(slugs)

  const [brk, setBrk] = useState<any>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle')

  // Set by the page template from its own page context. Absent in the editor and on
  // any site that does not build its pages under a locale prefix.
  const pageLocale: string | undefined = puck?.metadata?.locale

  useEffect(() => {
    if (isEditing) return
    const lang = resolveCurrentLanguage(pageLocale)
    const slug = (entries.find(e => e.code === lang)?.slug ?? '').trim()
    if (!slug) {
      setBrk(null)
      setStatus('idle')
      return
    }
    let cancelled = false
    setStatus('loading')
    loadBreakBySlug(slug)
      .then((data: any) => {
        if (cancelled) return
        setBrk(data)
        setStatus('idle')
      })
      .catch((e: any) => {
        if (cancelled) return
        console.warn('[Break block] failed to load break', e)
        setBrk(null)
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
    // Re-fetch when the configured slugs change (editor) or when the page's locale
    // does. The cookie is still read once per mount, which is enough: switching
    // language navigates, and a navigation to another locale changes pageLocale.
  }, [isEditing, pageLocale, JSON.stringify(entries)])

  if (isEditing) {
    return (
      <div
        style={{
          background: '#e5e7eb',
          color: '#374151',
          border: '1px dashed #9ca3af',
          borderRadius: 4,
          padding: '10px 14px',
          font: '12px/1.5 system-ui, sans-serif',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Break</div>
        {entries.map(e => (
          <div key={e.code} style={{ color: '#6b7280' }}>
            {LANGUAGE_NAMES[e.code] ?? e.code}: {e.slug || '— not set —'}
          </div>
        ))}
      </div>
    )
  }

  // No break for the current language, or it failed to load: render nothing.
  if (!brk) return null

  // embedMode: the block behaves like /embed/{slug} — guests are auto-registered
  // and never prompted to sign up. Default on: sites using this block
  // (beautyu/celebrando) have no account flow, and the signup modal would
  // dead-end the step. Turn off for sites with real accounts.
  return <BreakExperience brk={brk} isEmbed={embedMode} inline />
}

export const PuckBreak: ComponentConfig<BreakBlockProps> = {
  label: 'Break',
  defaultProps: {
    slugs: normalizeEntries([]),
    embedMode: true,
  },
  fields: {
    slugs: {
      type: 'custom' as const,
      label: 'Break slug per language',
      render: ({ value, onChange }: any) => (
        <BreakSlugField value={value ?? []} onChange={onChange} />
      ),
    },
    embedMode: {
      type: 'custom' as const,
      label: 'Embed mode',
      render: ({ value, onChange }: any) => {
        const on = value !== false
        return (
          <div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={e => onChange(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: '#4363d8' }}
              />
              <span style={{ fontWeight: 600 }}>
                Embed mode {on ? '(no account required)' : '(off)'}
              </span>
            </label>
            <div
              style={{
                fontSize: 11,
                lineHeight: 1.45,
                color: 'rgba(0,0,0,0.55)',
                marginTop: 4,
                paddingLeft: 24,
              }}
            >
              {on
                ? 'Visitors are silently registered as guests and never see the signup modal.'
                : 'Visitors must sign up before starting a step. Use only on sites with a real account flow.'}
            </div>
          </div>
        )
      },
    },
  },
  render: props => <BreakRender {...props} />,
}
